/**
 * Layout — slotting-core
 * Pure computation of the droplet capacity visual: instance blocks proportional
 * to memory, NUMA node grouping with separators, free space, and over-capacity
 * fallback. Ported from OutpostForge v1 js/visualizer.js with the DOM rendering
 * replaced by a data model (the React visualizer renders from this model).
 */

import { INSTANCE_CATALOG, INSTANCE_COLORS, NUMA_CONFIG } from './catalog.js';
import type { Droplet, FamilyData, InstanceSize, NumaConfig } from './types.js';

/** A block representing one instance, proportional to its memory. */
export interface InstanceBlock {
  kind: 'instance';
  typeName: string;
  spec: InstanceSize;
  /** Width as percentage of the containing element (node or droplet). */
  widthPct: number;
  color: string;
  /** Tooltip: "<type> | <vCPUs> vCPUs | <mem> GiB[ | <gpus> GPU]" */
  tooltip: string;
  /** True when the block spans NUMA nodes (rendered full-width). */
  spanning: boolean;
  /** True when the block crosses the capacity boundary (over-capacity layout). */
  exceeded: boolean;
}

/** A block representing unused memory within a node or droplet. */
export interface FreeSpaceBlock {
  kind: 'free';
  memoryGiB: number;
  widthPct: number;
  /** Tooltip: "Free: <mem> GiB" */
  tooltip: string;
}

export type LayoutBlock = InstanceBlock | FreeSpaceBlock;

/** Layout of a single NUMA node. */
export interface NumaNodeLayout {
  /** Width as percentage of the droplet: 100 / nodeCount. */
  widthPct: number;
  blocks: LayoutBlock[];
}

/** Complete visual layout of a droplet. */
export interface DropletLayout {
  /** 'numa': spanning blocks + NUMA nodes; 'flat': plain sequence of blocks. */
  mode: 'numa' | 'flat';
  /** True when used memory exceeds droplet capacity. */
  exceeded: boolean;
  /** Node-spanning instance blocks rendered before NUMA nodes (numa mode). */
  spanningBlocks: InstanceBlock[];
  /** NUMA node layouts (numa mode; empty when spanning consumes all memory). */
  numaNodes: NumaNodeLayout[];
  /** Number of NUMA separators between nodes (nodeCount - 1, or 0). */
  separatorCount: number;
  /** Tooltip for each separator. */
  separatorTitle: string;
  /** Blocks in flat mode (no NUMA config, or over capacity). */
  flatBlocks: LayoutBlock[];
}

/** One placed instance during NUMA distribution. */
export interface PlacedInstance {
  typeName: string;
  spec: InstanceSize;
}

/** Result of distributing instances across NUMA nodes. */
export interface NumaDistribution {
  nodes: PlacedInstance[][];
  spanning: PlacedInstance[];
}

// --- Color assignment ---

let _colorMap: Record<string, number> = {};
let _colorIndex = 0;

/**
 * Get a consistent color for a given instance type name.
 * Color is determined by the type's index within its family in the catalog,
 * so it never changes regardless of which types are active.
 */
export function getColorForType(typeName: string): string {
  const cached = _colorMap[typeName];
  if (cached !== undefined) {
    return INSTANCE_COLORS[cached % INSTANCE_COLORS.length] as string;
  }
  // Search all families in all generations for this type's index
  for (const families of Object.values(INSTANCE_CATALOG)) {
    for (const family of Object.values(families)) {
      for (let s = 0; s < family.sizes.length; s++) {
        if (family.sizes[s]?.name === typeName) {
          _colorMap[typeName] = s;
          return INSTANCE_COLORS[s % INSTANCE_COLORS.length] as string;
        }
      }
    }
  }
  // Fallback: use incremental index
  _colorMap[typeName] = _colorIndex;
  _colorIndex++;
  return INSTANCE_COLORS[(_colorMap[typeName] as number) % INSTANCE_COLORS.length] as string;
}

/** Reset the color assignment cache. */
export function resetColorMap(): void {
  _colorMap = {};
  _colorIndex = 0;
}

// --- Helpers ---

function makeTooltip(typeName: string, spec: InstanceSize): string {
  const gpuText = spec.gpus ? ' | ' + spec.gpus + ' GPU' : '';
  return typeName + ' | ' + spec.vCPUs + ' vCPUs | ' + spec.memoryGiB + ' GiB' + gpuText;
}

function makeInstanceBlock(
  typeName: string,
  spec: InstanceSize,
  widthPct: number,
  opts: { spanning?: boolean; exceeded?: boolean } = {},
): InstanceBlock {
  return {
    kind: 'instance',
    typeName,
    spec,
    widthPct,
    color: getColorForType(typeName),
    tooltip: makeTooltip(typeName, spec),
    spanning: opts.spanning ?? false,
    exceeded: opts.exceeded ?? false,
  };
}

function makeFreeSpaceBlock(memoryGiB: number, widthPct: number): FreeSpaceBlock {
  return {
    kind: 'free',
    memoryGiB,
    widthPct,
    tooltip: 'Free: ' + memoryGiB + ' GiB',
  };
}

// --- NUMA distribution ---

/**
 * Distribute instances across NUMA nodes using two-phase first-fit decreasing.
 *
 * Phase 1: Separate instances into node-spanning (vCPUs > nodeVCPUs) and
 *          node-constrained (vCPUs <= nodeVCPUs).
 * Phase 2: Calculate effective per-node capacity after deducting node-spanning
 *          vCPUs proportionally across all nodes.
 * Phase 3: Bin-pack node-constrained instances into nodes using first-fit
 *          decreasing with the reduced capacity.
 */
export function distributeToNUMANodes(
  droplet: Droplet,
  familyData: FamilyData,
  nodeCount: number,
  nodeVCPUs: number,
): NumaDistribution {
  // Build flat list of instances
  const allInstances: PlacedInstance[] = [];
  for (const [typeName, count] of Object.entries(droplet.instances)) {
    if (count <= 0) continue;
    const spec = familyData.sizes.find((s) => s.name === typeName);
    if (!spec) continue;
    for (let j = 0; j < count; j++) {
      allInstances.push({ typeName, spec });
    }
  }

  // Phase 1: Separate into node-spanning and node-constrained
  const spanning: PlacedInstance[] = [];
  const constrained: PlacedInstance[] = [];
  for (const inst of allInstances) {
    if (inst.spec.vCPUs > nodeVCPUs) {
      spanning.push(inst);
    } else {
      constrained.push(inst);
    }
  }

  // Phase 2: Calculate effective per-node capacity
  let totalSpanningVCPUs = 0;
  for (const inst of spanning) {
    totalSpanningVCPUs += inst.spec.vCPUs;
  }
  const effectiveNodeVCPUs = nodeVCPUs - totalSpanningVCPUs / nodeCount;

  // Phase 3: Sort node-constrained by vCPUs descending, then first-fit decreasing
  constrained.sort((a, b) => b.spec.vCPUs - a.spec.vCPUs);

  const nodes: PlacedInstance[][] = [];
  const nodeUsed: number[] = [];
  for (let n = 0; n < nodeCount; n++) {
    nodes.push([]);
    nodeUsed.push(0);
  }

  for (const inst of constrained) {
    for (let m = 0; m < nodeCount; m++) {
      if ((nodeUsed[m] ?? 0) + inst.spec.vCPUs <= effectiveNodeVCPUs) {
        nodes[m]?.push(inst);
        nodeUsed[m] = (nodeUsed[m] ?? 0) + inst.spec.vCPUs;
        break;
      }
    }
  }

  return { nodes, spanning };
}

// --- Layout computation ---

/**
 * Compute the visual layout of a droplet.
 * Generations with a NUMA config (and within memory capacity) get the NUMA
 * layout: spanning blocks first, then NUMA nodes with constrained instances
 * and per-node free space, separated by NUMA boundaries. Otherwise (no NUMA
 * config or over capacity) a flat layout is produced, marking blocks that
 * cross the capacity boundary as exceeded.
 */
export function computeDropletLayout(
  droplet: Droplet,
  familyData: FamilyData,
  generation: string,
  numaConfigMap: Record<string, NumaConfig> = NUMA_CONFIG,
): DropletLayout {
  const totalMemory = familyData.dropletCapacity.memoryGiB;
  const config = numaConfigMap[generation];
  const hasNUMA = !!config;
  const nodeCount = config ? config.nodeCount : 1;
  const nodeVCPUs = config ? config.vCPUsPerNode : familyData.dropletCapacity.vCPUs;

  // Calculate total used memory
  let usedMemory = 0;
  const entries = Object.entries(droplet.instances);
  for (const [typeName, count] of entries) {
    if (count <= 0) continue;
    const sizeSpec = familyData.sizes.find((s) => s.name === typeName);
    if (sizeSpec) usedMemory += sizeSpec.memoryGiB * count;
  }

  const exceeded = usedMemory > totalMemory;

  if (hasNUMA && !exceeded) {
    // NUMA layout
    const distribution = distributeToNUMANodes(droplet, familyData, nodeCount, nodeVCPUs);

    // Spanning memory determines per-node memory for constrained instances
    let spanningMemory = 0;
    for (const inst of distribution.spanning) {
      spanningMemory += inst.spec.memoryGiB;
    }

    // Node-spanning instances as full-width blocks first
    const spanningBlocks: InstanceBlock[] = distribution.spanning.map((inst) =>
      makeInstanceBlock(inst.typeName, inst.spec, (inst.spec.memoryGiB / totalMemory) * 100, {
        spanning: true,
      }),
    );

    // Per-node memory for constrained instances (after deducting spanning memory)
    const nodeMemory = (totalMemory - spanningMemory) / nodeCount;

    // If all instances are spanning (no constrained instances and no remaining
    // node memory), omit empty NUMA nodes and separators.
    const hasConstrainedInstances = distribution.nodes.some((node) => node.length > 0);

    if (!hasConstrainedInstances && nodeMemory <= 0) {
      return {
        mode: 'numa',
        exceeded: false,
        spanningBlocks,
        numaNodes: [],
        separatorCount: 0,
        separatorTitle: 'NUMA boundary',
        flatBlocks: [],
      };
    }

    // NUMA nodes with node-constrained instances
    const numaNodes: NumaNodeLayout[] = [];
    for (let n = 0; n < nodeCount; n++) {
      const nodeInstances = distribution.nodes[n] ?? [];
      // Sort within node by memory descending
      nodeInstances.sort((a, b) => b.spec.memoryGiB - a.spec.memoryGiB);

      const blocks: LayoutBlock[] = [];
      let nodeUsedMem = 0;
      for (const inst of nodeInstances) {
        blocks.push(
          makeInstanceBlock(inst.typeName, inst.spec, (inst.spec.memoryGiB / nodeMemory) * 100),
        );
        nodeUsedMem += inst.spec.memoryGiB;
      }

      // Free space in this node
      if (nodeUsedMem < nodeMemory) {
        blocks.push(
          makeFreeSpaceBlock(nodeMemory - nodeUsedMem, ((nodeMemory - nodeUsedMem) / nodeMemory) * 100),
        );
      }

      numaNodes.push({ widthPct: 100 / nodeCount, blocks });
    }

    return {
      mode: 'numa',
      exceeded: false,
      spanningBlocks,
      numaNodes,
      separatorCount: nodeCount - 1,
      separatorTitle: 'NUMA boundary',
      flatBlocks: [],
    };
  }

  // No NUMA config or exceeded: flat layout sorted by memory descending
  const denominator = exceeded ? usedMemory : totalMemory;
  let renderedMemory = 0;
  const flatBlocks: LayoutBlock[] = [];

  const sortedEntries = [...entries].sort((a, b) => {
    const specA = familyData.sizes.find((s) => s.name === a[0]);
    const specB = familyData.sizes.find((s) => s.name === b[0]);
    return (specB ? specB.memoryGiB : 0) - (specA ? specA.memoryGiB : 0);
  });

  for (const [typeName, count] of sortedEntries) {
    if (count <= 0) continue;
    const sizeSpec = familyData.sizes.find((s) => s.name === typeName);
    if (!sizeSpec) continue;

    for (let i = 0; i < count; i++) {
      const crossesBoundary = exceeded && renderedMemory + sizeSpec.memoryGiB > totalMemory;
      flatBlocks.push(
        makeInstanceBlock(typeName, sizeSpec, (sizeSpec.memoryGiB / denominator) * 100, {
          exceeded: crossesBoundary,
        }),
      );
      renderedMemory += sizeSpec.memoryGiB;
    }
  }

  if (!exceeded && usedMemory < totalMemory) {
    flatBlocks.push(
      makeFreeSpaceBlock(totalMemory - usedMemory, ((totalMemory - usedMemory) / denominator) * 100),
    );
  }

  return {
    mode: 'flat',
    exceeded,
    spanningBlocks: [],
    numaNodes: [],
    separatorCount: 0,
    separatorTitle: 'NUMA boundary',
    flatBlocks,
  };
}
